import { invalidateQuery, noddiAsync, URLKeys } from 'noddi-async';
import { NoddiButton, NoddiCard, NoddiInfoBanner, NoddiInput } from 'noddi-ui-web';
import { NoddiDateFormats, noddiFormatDate } from 'noddi-util';
import { useState } from 'react';
import QRCode from 'react-qr-code';
import { useAuthContext } from '../../contexts/useAuthContext';

type MfaManagementTranslations = {
  authenticatorApp: string;
  description: string;
  enabled: string;
  disabled: string;
  confirmedAt: string;
  setUp: string;
  cancel: string;
  scanQr: string;
  manualEntry: string;
  code: string;
  confirm: string;
  disable: string;
  disableCode: string;
  invalidCode: string;
  enrollFailed: string;
  statusLoadFailed: string;
};

interface MfaManagementProps {
  translations?: MfaManagementTranslations;
}

const defaultTexts: MfaManagementTranslations = {
  authenticatorApp: 'Authenticator app',
  description:
    'Use an authenticator app (such as Google Authenticator or 1Password) as a second factor when signing in.',
  enabled: 'Enabled',
  disabled: 'Not set up',
  confirmedAt: 'Enabled',
  setUp: 'Set up authenticator app',
  cancel: 'Cancel',
  scanQr: 'Scan this QR code with your authenticator app, then enter the generated code below.',
  manualEntry: 'Or enter this code manually',
  code: 'Authentication code',
  confirm: 'Confirm',
  disable: 'Disable',
  disableCode: 'Enter a current code to disable',
  invalidCode: 'Invalid code. Please try again.',
  enrollFailed: 'Failed to start setup',
  statusLoadFailed: 'Failed to load authenticator status'
};

export const MfaManagement = ({ translations }: MfaManagementProps) => {
  const { userData } = useAuthContext();
  const [enrollment, setEnrollment] = useState<{ provisioningUri: string; secret: string } | null>(null);
  const [code, setCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  const texts = translations ?? defaultTexts;

  const userId = userData?.user.id;

  const {
    data: statusData,
    error: statusError,
    isPending: isStatusPending
  } = noddiAsync.useGet({
    type: URLKeys.getTotpStatus,
    input: undefined,
    queryConfig: {
      enabled: !!userId
    }
  });

  const { mutateAsync: enroll, isPending: isEnrolling } = noddiAsync.usePost({
    type: URLKeys.postTotpEnroll,
    queryConfig: {
      onSuccess: (response) => {
        setEnrollment(response.data);
        setError(null);
      },
      onError: () => setError(texts.enrollFailed)
    }
  });

  const { mutateAsync: confirmTotp, isPending: isConfirming } = noddiAsync.usePost({
    type: URLKeys.postTotpConfirm,
    queryConfig: {
      onSuccess: () => {
        setEnrollment(null);
        setCode('');
        setError(null);
        invalidateQuery({ urlKey: URLKeys.getTotpStatus });
      },
      onError: () => setError(texts.invalidCode)
    }
  });

  const { mutateAsync: disable, isPending: isDisabling } = noddiAsync.usePost({
    type: URLKeys.postTotpDisable,
    queryConfig: {
      onSuccess: () => {
        setDisableCode('');
        setError(null);
        invalidateQuery({ urlKey: URLKeys.getTotpStatus });
      },
      onError: () => setError(texts.invalidCode)
    }
  });

  if (!userId) {
    return null;
  }

  const enabled = statusData?.enabled ?? false;

  return (
    <NoddiCard title={texts.authenticatorApp} description={texts.description}>
      <div className='flex flex-col gap-4'>
        {error && <NoddiInfoBanner variant='error' texts={[{ value: error }]} />}
        {statusError && <NoddiInfoBanner variant='error' texts={[{ value: texts.statusLoadFailed }]} />}

        {!isStatusPending && !enrollment && (
          <div className='flex items-center justify-between rounded-lg border p-3'>
            <div className='flex flex-col'>
              <span className='font-medium'>{enabled ? texts.enabled : texts.disabled}</span>
              {enabled && statusData?.confirmedAt && (
                <span className='text-sm text-muted-foreground'>
                  {texts.confirmedAt}: {noddiFormatDate(statusData.confirmedAt, NoddiDateFormats.DATE)}
                </span>
              )}
            </div>
            {!enabled && (
              // oxlint-disable-next-line typescript/no-misused-promises -- async onClick runs enroll(); React discards the returned promise, so a rejection is unhandled unless the body catches
              <NoddiButton loading={isEnrolling} disabled={isEnrolling} onClick={() => enroll({})}>
                {texts.setUp}
              </NoddiButton>
            )}
          </div>
        )}

        {enrollment && (
          <div className='flex flex-col gap-3 rounded-lg border p-4'>
            <p className='text-sm'>{texts.scanQr}</p>
            <div className='rounded-lg bg-white p-4'>
              <QRCode value={enrollment.provisioningUri} size={160} />
            </div>
            <div className='flex flex-col gap-1'>
              <span className='text-sm text-muted-foreground'>{texts.manualEntry}</span>
              <span className='rounded-sm bg-muted px-2 py-1 font-mono text-sm'>{enrollment.secret}</span>
            </div>
            <NoddiInput
              value={code}
              onChange={(e) => setCode(e.target.value)}
              label={texts.code}
              autoComplete='one-time-code'
              disabled={isConfirming}
            />
            <div className='flex gap-2'>
              <NoddiButton
                loading={isConfirming}
                disabled={isConfirming || !code.trim()}
                // oxlint-disable-next-line typescript/no-misused-promises -- async onClick runs confirmTotp(); React discards the returned promise, so a rejection is unhandled unless the body catches
                onClick={() => confirmTotp({ code: code.trim() })}
              >
                {texts.confirm}
              </NoddiButton>
              <NoddiButton
                variant='outline'
                disabled={isConfirming}
                onClick={() => {
                  setEnrollment(null);
                  setCode('');
                  setError(null);
                }}
              >
                {texts.cancel}
              </NoddiButton>
            </div>
          </div>
        )}

        {enabled && !enrollment && (
          <div className='flex flex-col gap-2 rounded-lg border p-4 md:flex-row md:items-end'>
            <div className='flex-1'>
              <NoddiInput
                value={disableCode}
                onChange={(e) => setDisableCode(e.target.value)}
                label={texts.disableCode}
                autoComplete='one-time-code'
                disabled={isDisabling}
              />
            </div>
            <NoddiButton
              variant='destructive'
              loading={isDisabling}
              disabled={isDisabling || !disableCode.trim()}
              // oxlint-disable-next-line typescript/no-misused-promises -- async onClick runs disable(); React discards the returned promise, so a rejection is unhandled unless the body catches
              onClick={() => disable({ code: disableCode.trim() })}
            >
              {texts.disable}
            </NoddiButton>
          </div>
        )}
      </div>
    </NoddiCard>
  );
};
